/**
 * F1 / F2 one-keystroke order entry — ROADMAP 4.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE KEYS BEHAVE CAREFULLY
 *
 * A keystroke that opens an order ticket is a keystroke that must not fire
 * while the user is typing into one. So the handler ignores the event when
 * focus is in a field — including, specifically, the LIVE confirmation box,
 * where an F-key firing a second ticket over the top would be its own kind
 * of disaster.
 *
 * `preventDefault` matters: F1 is Help in every browser, and a help panel
 * opening over a trading screen during a move is worse than the lost
 * shortcut.
 *
 * NO MODIFIERS. A combination the OS or the browser already owns would work
 * on one machine and not another, and a shortcut that silently does nothing
 * on a colleague's laptop is worse than no shortcut.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect } from 'react'
import type { OrderSideValue } from '@apna/shared'

export interface ChainKeyBindings {
  /** F1 — buy the highlighted strike. */
  onBuy: () => void
  /** F2 — sell it. */
  onSell: () => void
  /** False while a ticket is open, so a key cannot stack a second one. */
  enabled: boolean
}

/** Is the user typing? Then this keystroke is theirs, not ours. */
export function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  /*
   * Compared against true rather than returned directly. `lib.dom` types
   * `isContentEditable` as boolean, but it is absent in some environments
   * and the property then yields `undefined` — which TypeScript cannot see
   * and which would make this function return a non-boolean from a
   * signature that promises one.
   */
  return target.isContentEditable === true
}

/** Which side a key means, or null when the key is not ours. */
export function sideForKey(key: string): OrderSideValue | null {
  if (key === 'F1') return 'BUY'
  if (key === 'F2') return 'SELL'
  return null
}

export function useChainKeys({ onBuy, onSell, enabled }: ChainKeyBindings): void {
  useEffect(() => {
    if (!enabled) return

    const handler = (e: KeyboardEvent): void => {
      const side = sideForKey(e.key)
      if (!side) return
      // A modifier means the user meant something else — browser or OS.
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return
      if (isEditingTarget(e.target)) return

      // F1 is Help. A help panel over a trading screen during a move is
      // worse than the lost shortcut.
      e.preventDefault()
      if (side === 'BUY') onBuy()
      else onSell()
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onBuy, onSell, enabled])
}
